/**
 * Sign in — TWO entrances, split on the one thing that changes the form.
 *
 *   /login              ·  /school/:slug/         students
 *   /staff              ·  /school/:slug/staff    everyone else
 *
 * The split is the IDENTIFIER, and nothing else. School students sign in with a
 * school-local ID (a roll or admission number), which is what makes "1234" mean a
 * different student at two schools; everyone else uses an email, because a
 * self-managed credential has to work across workspaces. The surrounding links
 * follow from the same fact — only a first-party student can create an account,
 * and only a self-managed password has a reset flow.
 *
 * There used to be a third box at `/admin`. It asked for an email and a password,
 * exactly as this one does, so the only thing it added was a second address for
 * the same credential — and a school admin who typed `/staff`, the address on
 * every other staff instruction, met a page that looked like the wrong door.
 * Teachers, counsellors, school admins and platform operators now share one
 * entrance, which is also the honest shape: an admin is staff.
 *
 * `/staff` deliberately does NOT ask which kind of staff you are. A person holds
 * exactly one role per workspace and `GET /me` reports it, so the answer could only
 * ever be ignored or contradicted — a control that cannot change the outcome is a
 * control that misleads.
 *
 * None of it grants anything. The box picks a form and a heading; where someone
 * lands is decided by that membership, and what they may do is re-derived by the
 * API on every request. Signing in at /staff does not make anyone an admin, and
 * `/admin` and `/teacher` are consoles only — signed out they redirect here.
 */
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { AccountDTO } from '@cwy/config/contracts';
import { Button } from '@/components/ds';
import {
  AUTH_BUTTON_STYLE,
  AuthError,
  AuthHeading,
  AuthLayout,
  TextField,
} from '@/components/auth/AuthLayout';
import { apiGet, apiPost } from '@/lib/api';
import { landingPathForAccount, surfaceForRole } from '@/lib/auth';
import { queryClient } from '@/lib/queryClient';
import { ACCOUNT_KEY } from '@/hooks/useSession';
import { signIn } from '@/lib/session';
import { surfaceForPath, tenant, tenantPath, type LoginBox } from '@/lib/tenant';
import { useTenantInfo } from '@/hooks/useTenant';
import { fontPx } from '@/lib/fontSize';

interface SchoolLoginResponse {
  userId: string;
  role: string;
  tenantSlug: string;
  mustChangePassword: boolean;
  viaDevelopmentBypass: boolean;
}

interface Copy {
  title: string;
  blurb: string;
}

function copyFor(box: LoginBox, useLocalId: boolean, schoolName: string | null): Copy {
  if (box === 'STAFF') {
    // One heading for teachers and administrators alike. It cannot name the role
    // — the role is not known until after sign-in — and it does not need to: the
    // instruction is the same either way.
    return schoolName
      ? { title: `${schoolName} — staff sign in`, blurb: 'Use your work email address.' }
      : { title: 'Staff sign in', blurb: 'Use the email address your account was created with.' };
  }
  if (schoolName) {
    return {
      title: `Sign in to ${schoolName}`,
      blurb: useLocalId
        ? 'Use the ID and password your school gave you.'
        : 'Use your email address.',
    };
  }
  return { title: 'Welcome back', blurb: 'Sign in to pick up where you left off.' };
}

export function LoginPage({ box }: { box: LoginBox }) {
  const navigate = useNavigate();
  const location = useLocation();
  const info = useTenantInfo();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Set by the guards when they bounce someone away from a page they asked for.
  const from = (location.state as { from?: string } | null)?.from;

  // Only a school looks up branding and login policy. At the root domain the
  // query is disabled, and a disabled react-query stays `pending` forever — which
  // is exactly why gating on `isPending` once left /admin stuck on "Loading…".
  const needsSchool = Boolean(tenant.slug);
  const schoolName = needsSchool ? (info.data?.name ?? null) : null;

  // Staff sign in by email even at a school URL; only the student surface uses a
  // school-issued ID, and only where the tenant actually issues them.
  const useLocalId = box === 'STUDENT' && Boolean(info.data?.acceptsLocalIdentifier);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');

    try {
      if (useLocalId) {
        const result = await apiPost<SchoolLoginResponse>(
          `/auth/school/${encodeURIComponent(tenant.slug!)}/login`,
          { loginId: identifier.trim(), password },
        );
        /*
         * A HARD navigation, not a router push.
         *
         * This endpoint is ours, not Better Auth's, so it sets the session
         * cookie without Better Auth's client store ever hearing about it.
         * `useSession()` would still read "signed out", and RequireAuth would
         * bounce straight back to this page — a login that appears to do
         * nothing.
         *
         * Reloading rebuilds every client-side cache from the cookie that now
         * exists, and it also re-resolves the tenant, which this app does once
         * per page load by design.
         */
        window.location.assign(
          tenantPath(result.mustChangePassword ? '/change-password' : '/today'),
        );
        return;
      }

      await signIn({ email: identifier.trim(), password });

      /*
       * Read the account before choosing a destination.
       *
       * The surface in the URL is a hint, and a correct password proves nothing
       * about which console this person owns. Sending them to this surface's home
       * regardless would drop a student on /admin, where the surface gate answers
       * "not found" — a dead end reached with valid credentials. Their own
       * landing path is the honest answer, and seeding the cache with the same
       * response spares AuthGate a second round trip.
       */
      const account = await apiGet<AccountDTO>('/me');
      queryClient.setQueryData(ACCOUNT_KEY, account);
      const ownSurface = surfaceForRole(account.platformRole, account.membership?.role);

      // A remembered destination is honoured only when it belongs to the surface
      // this person actually holds; otherwise it is the same dead end.
      const target =
        from && surfaceForPath(from) === ownSurface ? from : landingPathForAccount(account);
      navigate(target, { replace: true });
    } catch (err) {
      // The server returns one generic message for every failure — unknown ID,
      // wrong password, suspended membership — and this page must not add detail
      // it does not have.
      setError(err instanceof Error ? err.message : 'Could not sign you in.');
      setBusy(false);
    }
  };

  if (needsSchool && info.isPending) {
    return (
      <AuthLayout>
        <p className="text-ink-soft">Loading…</p>
      </AuthLayout>
    );
  }

  // A slug that does not resolve — suspended, archived or never existed. All
  // three look identical here on purpose.
  if (needsSchool && (info.isError || !info.data)) {
    return (
      <AuthLayout>
        <AuthHeading title="This school link doesn’t work">
          Check the address with your school, or sign in at the main CWY site.
        </AuthHeading>
        <a href="/login" style={{ fontSize: fontPx(14) }}>
          Go to CWY sign in
        </a>
      </AuthLayout>
    );
  }

  const copy = copyFor(box, useLocalId, schoolName);

  return (
    <AuthLayout>
      <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
        <AuthHeading title={copy.title}>{copy.blurb}</AuthHeading>

        <AuthError>{error}</AuthError>

        <TextField
          label={useLocalId ? 'Roll or admission number' : 'Email address'}
          type={useLocalId ? 'text' : 'email'}
          name={useLocalId ? 'username' : 'email'}
          autoComplete={useLocalId ? 'username' : 'email'}
          inputMode={useLocalId ? 'text' : 'email'}
          autoFocus
          required
          placeholder={useLocalId ? 'e.g. 1234' : 'you@example.com'}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          disabled={busy}
        />

        <div>
          <TextField
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
          {/* Only self-managed accounts have a reset flow. A school-managed
              student's password is reset by their school, so offering them a
              "forgot password" link would send them to a page that cannot help. */}
          {useLocalId ? (
            <p className="mt-2 text-sm text-ink-soft">
              Forgotten your password? Ask your class teacher to reset it.
            </p>
          ) : (
            <div className="mt-2 text-right text-sm">
              <Link
                to="/forgot-password"
                className="font-semibold text-forest underline-offset-2 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          )}
        </div>

        <Button type="submit" style={AUTH_BUTTON_STYLE} disabled={busy || !identifier || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>

        {/* Self sign-up is a first-party student affordance only. A school issues
            its own accounts, and nobody creates a teacher or admin account for
            themselves. */}
        {box === 'STUDENT' && !needsSchool && (
          <p className="text-center text-sm text-ink-soft">
            New to CWY?{' '}
            <Link
              to="/signup"
              className="font-semibold text-forest underline-offset-2 hover:underline"
            >
              Create an account
            </Link>
          </p>
        )}
      </form>
    </AuthLayout>
  );
}

export default LoginPage;
